import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { NavIcon } from '@/components/icons/NavIcon';

import { roleTone } from './roleOptions';
import type { RoleOption } from './roleOptions';
import styles from './RoleSelect.module.css';

// 사용자 한 명의 역할 다중 선택 — 팝업 안에서 고른 뒤 '적용'으로 한 번에 PATCH 한다.
// 체크할 때마다 저장하면 중간 상태(예: 역할 0개 → guest 권한 회수)가 서버에 반영될 수 있어서다.
// 테이블이 overflow:hidden 이라 팝업은 body 포털 + fixed 좌표로 띄운다.

interface RoleSelectProps {
  value: string[];
  options: RoleOption[];
  onApply: (next: string[]) => void;
  disabled?: boolean;
  // 본인 행 — admin 체크 해제를 막는다(서버도 400).
  lockAdmin?: boolean;
}

const POPUP_WIDTH = 260;
const POPUP_GAP = 6;

export function RoleSelect({ value, options, onApply, disabled = false, lockAdmin = false }: RoleSelectProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(value);
  const [pos, setPos] = useState<{ top: number; left: number; up: boolean } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const id = useId();

  // 트리거 기준 좌표 — 아래 공간이 부족하면 위로 연다. 스크롤·리사이즈 때도 트리거를 따라간다.
  const place = useCallback(() => {
    if (!trigger.current) return;
    const rect = trigger.current.getBoundingClientRect();
    const height = popup.current?.offsetHeight ?? 0;
    const up = rect.bottom + POPUP_GAP + height > window.innerHeight && rect.top > height + POPUP_GAP;
    setPos({
      top: up ? rect.top - POPUP_GAP - height : rect.bottom + POPUP_GAP,
      left: Math.max(8, rect.right - POPUP_WIDTH),
      up,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, draft.length, place]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (trigger.current?.contains(target) || popup.current?.contains(target)) return;
      setOpen(false);
    };
    // 포커스가 트리거·팝업 어디에 있든 Esc 로 닫는다.
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  const selected = options.filter((o) => value.includes(o.key));
  const changed = draft.length !== value.length || draft.some((k) => !value.includes(k));

  const toggle = (key: string, checked: boolean) => {
    const next = new Set(draft);
    if (checked) next.add(key);
    else next.delete(key);
    // 옵션 순서를 유지해 저장 값이 매번 같은 순서가 되게 한다.
    setDraft(options.filter((o) => next.has(o.key)).map((o) => o.key));
  };

  const apply = () => {
    setOpen(false);
    if (changed) onApply(draft);
  };

  return (
    <>
      <button
        type="button"
        ref={trigger}
        className={styles.trigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => {
          // 열 때만 서버 값으로 드래프트를 맞춘다 — 닫으면 미적용 선택은 버린다.
          if (!open) setDraft(value);
          setOpen(!open);
        }}
        disabled={disabled}
        title={selected.map((o) => o.label).join(', ') || 'Guest'}
      >
        <span className={styles.chips}>
          {selected.length === 0 ? (
            <span className={`${styles.chip} ${styles.toneGuest}`}>
              <span className={styles.dot} />
              Guest
            </span>
          ) : (
            selected.map((o) => (
              <span key={o.key} className={`${styles.chip} ${styles[roleTone(o)]}`}>
                <span className={styles.dot} />
                {o.label}
              </span>
            ))
          )}
        </span>
        <span className={`${styles.chev} ${open ? styles.chevOpen : ''}`} aria-hidden>
          <NavIcon name="chev-d" size={13} />
        </span>
      </button>
      {open &&
        createPortal(
          <div
            id={id}
            ref={popup}
            role="dialog"
            aria-label="역할 선택"
            className={`${styles.popup} ${pos?.up ? styles.popupUp : ''}`}
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: POPUP_WIDTH }}
          >
            <div className={styles.popupHead}>역할 선택 · 여러 개 가능</div>
            <div className={styles.optionList}>
              {options.map((o) => {
                const checked = draft.includes(o.key);
                const locked = lockAdmin && o.key === 'admin' && value.includes('admin');
                return (
                  <label key={o.key} className={`${styles.option} ${locked ? styles.optionLocked : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={locked}
                      onChange={(e) => toggle(o.key, e.target.checked)}
                    />
                    <span className={`${styles.optionDot} ${styles[roleTone(o)]}`} />
                    <span className={styles.optionText}>
                      <span className={styles.optionLabel}>{o.label}</span>
                      {(locked || o.description) && (
                        <span className={styles.optionDesc}>
                          {locked ? '본인의 Admin 권한은 해제할 수 없어요' : o.description}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className={styles.popupFoot}>
              <span className={styles.footHint}>
                {draft.length === 0 ? '모두 해제하면 Guest — BigQuery 권한도 회수돼요' : `${draft.length}개 선택`}
              </span>
              <button type="button" className={styles.cancelBtn} onClick={() => setOpen(false)}>
                취소
              </button>
              <button type="button" className={styles.applyBtn} onClick={apply} disabled={!changed}>
                적용
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
